import type { Schemas } from '@kanos/api-client';
import { Button, DateField, SegmentedControl, SelectField, TextField, useToast } from '@kanos/ui';
import {
  api,
  Card,
  failure,
  formatDateTime,
  ProblemAlert,
  QueryContent,
  todayIso,
} from '@kanos/web-core';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { HOURS, isoOf } from './hours';

type MaintenanceNotice = Schemas['MaintenanceNotice'];

const noticeKey = ['platform', 'maintenance'] as const;

const noticeQuery = queryOptions({
  queryKey: noticeKey,
  queryFn: async (): Promise<MaintenanceNotice | null> => {
    const { data, error, response } = await api.GET('/api/v1/platform/maintenance');
    if (!data) throw failure('getPlatformMaintenanceNotice', error, response.status);
    return data.notice;
  },
});

function useChange() {
  const queryClient = useQueryClient();
  const settled = () => queryClient.invalidateQueries({ queryKey: ['maintenance'] });
  const set = useMutation({
    mutationFn: async (notice: MaintenanceNotice) => {
      const { data, error, response } = await api.PUT('/api/v1/platform/maintenance', {
        body: notice,
      });
      if (!data) throw failure('setMaintenanceNotice', error, response.status);
      return data.notice;
    },
    onSuccess: async (notice) => {
      queryClient.setQueryData(noticeKey, notice);
      await settled();
    },
  });
  const remove = useMutation({
    mutationFn: async () => {
      const { error, response } = await api.DELETE('/api/v1/platform/maintenance');
      if (!response.ok) throw failure('removeMaintenanceNotice', error, response.status);
    },
    onSuccess: async () => {
      queryClient.setQueryData(noticeKey, null);
      await settled();
    },
  });
  return { set, remove };
}

/**
 * The notice of the Kanos team to everyone (after Corly's maintenance banner): what, how
 * serious, from when until when - shown under the bar of every app while it is in force.
 */
export function MaintenancePage() {
  const notice = useQuery(noticeQuery);
  return (
    <Card
      id="maintenance"
      title="Hinweis an alle"
      description="Erscheint in der Kanzlei-App, im Mandantenportal und hier unter der Leiste – vom Beginn bis zum Ende. Ohne Ende bleibt er, bis Sie ihn entfernen."
    >
      <QueryContent query={notice}>{(current) => <NoticeForm current={current} />}</QueryContent>
    </Card>
  );
}

function NoticeForm({ current }: { current: MaintenanceNotice | null }) {
  const toast = useToast();
  const { set, remove } = useChange();
  const hour = String(new Date().getHours());
  const [message, setMessage] = useState(current?.message ?? '');
  const [tone, setTone] = useState<MaintenanceNotice['tone']>(current?.tone ?? 'warning');
  const [from, setFrom] = useState({ day: todayIso(), hour });
  const [ending, setEnding] = useState<'open' | 'until'>('until');
  const [until, setUntil] = useState({ day: todayIso(), hour: String((Number(hour) + 2) % 24) });

  return (
    <div className="grid gap-4">
      {current && (
        <p className="text-sm text-muted">
          Gesetzt: „{current.message}“ – ab {formatDateTime(current.startsAt)}
          {current.endsAt ? ` bis ${formatDateTime(current.endsAt)}` : ', ohne Ende'}.
        </p>
      )}
      <TextField
        label="Text"
        placeholder="z. B. Wartung am Sonntag von 8 bis 9 Uhr – Kanos ist dann nicht erreichbar."
        value={message}
        onChange={setMessage}
        multiline
        rows={3}
        isRequired
      />
      <div className="grid gap-2">
        <span className="text-sm font-medium">Art</span>
        <SegmentedControl
          aria-label="Art"
          options={[
            { id: 'warning', label: 'Störung oder Wartung' },
            { id: 'info', label: 'Hinweis' },
          ]}
          value={tone}
          onChange={setTone}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <DateField
          label="Beginn"
          value={from.day}
          onChange={(day) => day && setFrom({ ...from, day })}
        />
        <SelectField
          label="Uhrzeit"
          placeholder="Stunde wählen"
          options={HOURS}
          value={from.hour}
          onChange={(h) => setFrom({ ...from, hour: h })}
        />
      </div>
      <div className="grid gap-2">
        <span className="text-sm font-medium">Ende</span>
        <SegmentedControl
          aria-label="Ende"
          options={[
            { id: 'until', label: 'Mit Ende' },
            { id: 'open', label: 'Ohne Ende' },
          ]}
          value={ending}
          onChange={setEnding}
        />
      </div>
      {ending === 'until' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            label="Ende am"
            value={until.day}
            onChange={(day) => day && setUntil({ ...until, day })}
          />
          <SelectField
            label="Uhrzeit"
            placeholder="Stunde wählen"
            options={HOURS}
            value={until.hour}
            onChange={(h) => setUntil({ ...until, hour: h })}
          />
        </div>
      )}
      {set.error ? <ProblemAlert error={set.error} /> : null}
      {remove.error ? <ProblemAlert error={remove.error} /> : null}
      <div className="flex flex-wrap justify-end gap-2">
        {current && (
          <Button
            variant="secondary"
            isPending={remove.isPending}
            onPress={() =>
              remove.mutate(undefined, {
                onSuccess: () => toast({ tone: 'success', title: 'Hinweis entfernt' }),
              })
            }
          >
            Hinweis entfernen
          </Button>
        )}
        <Button
          isDisabled={message.trim() === ''}
          isPending={set.isPending}
          onPress={() =>
            set.mutate(
              {
                message: message.trim(),
                tone,
                startsAt: isoOf(from.day, from.hour),
                endsAt: ending === 'until' ? isoOf(until.day, until.hour) : null,
              },
              { onSuccess: () => toast({ tone: 'success', title: 'Hinweis gesetzt' }) },
            )
          }
        >
          Hinweis setzen
        </Button>
      </div>
    </div>
  );
}
